import { useCallback, useMemo, useRef, useState } from 'react';
import type { components } from '@adiox/api';
import { useTradeClient, useTradeGate } from '../trading/useTrading';
import { newIdempotencyKey, sideAllowed, type OrderType, type Side } from '../trading/tradeRules';
import { linesFor, resolveDrag, snapPrice, type ChartLine, type SymbolPrecision } from './chartTrading';

type SymbolInfo = components['schemas']['SymbolInfo'];
export type Notice = { tone: 'ok' | 'error'; text: string } | null;

/**
 * Everything a chart does with the account: the lines it draws, what dropping a line means, and the actions offered on
 * right-click and by the one-click widget. Failures come back as a message; the caller shows it and the line snaps back.
 */
export function useChartTrading(symbol: string, info: SymbolInfo | undefined) {
  const client = useTradeClient();
  const gate = useTradeGate(symbol);
  const account = gate.account;
  const [notice, setNotice] = useState<Notice>(null);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const attempt = useRef<{ key: string; body: string } | null>(null);

  const positions = useMemo(() => Object.values(account?.positions ?? {}), [account?.positions]);
  const orders = useMemo(() => Object.values(account?.orders ?? {}), [account?.orders]);
  const lines = useMemo(() => linesFor(symbol, positions, orders), [symbol, positions, orders]);
  const precision: SymbolPrecision = useMemo(
    () => ({ digits: info?.digits ?? 5, tickSize: info?.tickSize ?? 0 }),
    [info?.digits, info?.tickSize],
  );
  // Modifying needs the account and the connection, not a fresh price.
  const online = (account?.loaded ?? false) && gate.hubOnline && !gate.readOnly;

  const report = useCallback((outcome: { ok: true } | { ok: false; message: string }, success: string) => {
    setNotice(outcome.ok ? { tone: 'ok', text: success } : { tone: 'error', text: outcome.message });
    return outcome.ok;
  }, []);

  /** Releasing a dragged line: work out what it means and send it. Resolves true when the server accepted it. */
  const dropLine = useCallback(
    async (line: ChartLine, price: number): Promise<boolean> => {
      if (!client) return false;
      const outcome = resolveDrag(line, price, positions, orders, precision);
      if (outcome.action === 'none') {
        if (outcome.reason !== 'unchanged') setNotice({ tone: 'error', text: outcome.reason });
        return false;
      }
      setBusy(true);
      const digits = precision.digits;
      if (outcome.action === 'modifyPosition') {
        const done = await client.modifyPosition(outcome.ticket, outcome.stopLoss, outcome.takeProfit);
        setBusy(false);
        return report(done, `${outcome.what} of #${outcome.ticket} set to ${price.toFixed(digits)}.`);
      }
      const order = orders.find((o) => o.ticket === outcome.ticket);
      if (!order) {
        setBusy(false);
        return false;
      }
      const done = await client.modifyOrderFrom(order, {
        price: outcome.price,
        stopLoss: outcome.stopLoss,
        takeProfit: outcome.takeProfit,
      });
      setBusy(false);
      return report(done, `${outcome.what} of order #${outcome.ticket} set to ${price.toFixed(digits)}.`);
    },
    [client, positions, orders, precision, report],
  );

  /** Removes a stop loss or take profit line (sets it to 0), leaving the other level as it is. */
  const removeLevel = useCallback(
    async (line: ChartLine) => {
      if (!client) return;
      setBusy(true);
      if (line.kind === 'positionSl' || line.kind === 'positionTp') {
        const p = positions.find((x) => x.ticket === line.ticket);
        if (p)
          report(
            await client.modifyPosition(
              p.ticket,
              line.kind === 'positionSl' ? 0 : p.stopLoss,
              line.kind === 'positionTp' ? 0 : p.takeProfit,
            ),
            `${line.kind === 'positionSl' ? 'Stop loss' : 'Take profit'} of #${p.ticket} removed.`,
          );
      } else {
        const o = orders.find((x) => x.ticket === line.ticket);
        if (o)
          report(
            await client.modifyOrderFrom(o, line.kind === 'orderSl' ? { stopLoss: 0 } : { takeProfit: 0 }),
            `${line.kind === 'orderSl' ? 'Stop loss' : 'Take profit'} of order #${o.ticket} removed.`,
          );
      }
      setBusy(false);
    },
    [client, positions, orders, report],
  );

  const closePosition = useCallback(
    async (ticket: number) => {
      if (!client) return;
      setBusy(true);
      report(await client.closePosition(ticket), `Position #${ticket} closed.`);
      setBusy(false);
    },
    [client, report],
  );

  const cancelOrder = useCallback(
    async (ticket: number) => {
      if (!client) return;
      setBusy(true);
      report(await client.cancelOrder(ticket), `Order #${ticket} cancelled.`);
      setBusy(false);
    },
    [client, report],
  );

  /** Sets the stop loss or take profit of a position to a price picked on the chart. */
  const setStopAt = useCallback(
    async (ticket: number, target: 'stopLoss' | 'takeProfit', price: number) => {
      if (!client) return;
      const p = positions.find((x) => x.ticket === ticket);
      if (!p) return;
      setBusy(true);
      report(
        await client.modifyPosition(
          ticket,
          target === 'stopLoss' ? price : p.stopLoss,
          target === 'takeProfit' ? price : p.takeProfit,
        ),
        `${target === 'stopLoss' ? 'Stop loss' : 'Take profit'} of #${ticket} set to ${price.toFixed(precision.digits)}.`,
      );
      setBusy(false);
    },
    [client, positions, precision.digits, report],
  );

  const placePending = useCallback(
    async (type: OrderType, price: number, volume: number): Promise<boolean> => {
      if (!client) return false;
      setBusy(true);
      const done = await client.placeOrder({ symbol, type, volume, price: snapPrice(price, precision) });
      setBusy(false);
      return report(done, `${type} placed at ${price.toFixed(precision.digits)}.`);
    },
    [client, symbol, precision, report],
  );

  /**
   * One-click market order. Exactly one request per click: further clicks are ignored while one is running, and the key of
   * an attempt with an unknown result is reused if the user clicks again, so the server still trades only once.
   */
  const oneClick = useCallback(
    async (side: Side, volume: number) => {
      if (!client || inFlight.current || gate.reason || !info || !sideAllowed(info.tradeMode, side)) return;
      inFlight.current = true; // a ref, so two clicks in the same instant cannot both pass
      setBusy(true);
      const body = `${symbol}|${side}|${volume}`;
      if (attempt.current?.body !== body) attempt.current = { key: newIdempotencyKey(), body };
      const done = await client.placeOrder({ symbol, type: side, volume }, attempt.current.key);
      inFlight.current = false;
      setBusy(false);
      if (done.ok || done.retcodeName) attempt.current = null; // a definite answer: the next click is a new order
      report(
        done,
        done.ok
          ? `${side} ${volume} ${symbol} at ${done.value.price.toFixed(precision.digits)}: position #${done.value.position}.`
          : '',
      );
    },
    [client, gate.reason, info, symbol, precision.digits, report],
  );

  return {
    ...gate,
    lines,
    positions,
    orders,
    precision,
    notice,
    setNotice,
    busy,
    online,
    dropLine,
    removeLevel,
    closePosition,
    cancelOrder,
    setStopAt,
    placePending,
    oneClick,
  };
}
